// Developer: Ankit Gupta
/**
 * The language a person picks on the sign-in pages is the language the product opens in.
 *
 * Keycloak keeps that pick on the account (the user attribute `locale`, carried in the token as
 * the `locale` claim) only when the person chooses it on its pages. So the two places a language
 * can be chosen are kept as one: a fresh sign-in takes the token's language, and a switch inside
 * the product is written back to the account, so the next sign-in page opens in it and the token
 * that follows does not carry an older choice back.
 */
import type Keycloak from 'keycloak-js';
import type { Language } from '@/i18n';

// Read before keycloak.init() strips Keycloak's answer from the address: only a page that came
// straight back from the sign-in or registration pages carries one.
const answer = typeof window === 'undefined' ? '' : `${window.location.hash}&${window.location.search}`;
export const cameFromSignIn = /[#?&]state=/.test(answer) && /[#?&](code|error)=/.test(answer);

/** The product's language for a token's `locale` claim ("ar", "ar-SA", "en-GB"), or null. */
export function languageOfClaim(claim: unknown): Language | null {
  if (typeof claim !== 'string') return null;
  const tag = claim.trim().toLowerCase();
  if (tag === 'ar' || tag.startsWith('ar-')) return 'ar';
  if (tag === 'en' || tag.startsWith('en-')) return 'en';
  return null;
}

/**
 * Write the language to the person's own Keycloak account (the Account API, with their own
 * token: no administrator rights). Best effort: on failure the product is switched anyway and the
 * next sign-in page simply opens in the language the product asks for.
 */
export async function saveOnAccount(kc: Keycloak | null, language: Language): Promise<void> {
  if (!kc?.authenticated || !kc.token || !kc.authServerUrl || !kc.realm) return;
  const url = `${kc.authServerUrl.replace(/\/$/, '')}/realms/${encodeURIComponent(kc.realm)}/account/`;
  const headers = { Authorization: `Bearer ${kc.token}`, Accept: 'application/json' };
  const current = await fetch(url, { headers });
  if (!current.ok) return;
  // The account is sent back whole with only the language changed: a partial one would clear
  // what it leaves out.
  const account = (await current.json()) as { attributes?: Record<string, unknown> };
  account.attributes = { ...(account.attributes ?? {}), locale: [language] };
  await fetch(url, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(account),
  });
}
